import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface HelpFeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  articleId: string;
  onSubmit: () => void;
  hasExistingVote?: boolean;
}

const HelpFeedbackDialog = ({ open, onOpenChange, articleId, onSubmit, hasExistingVote = false }: HelpFeedbackDialogProps) => {
  const [feedback, setFeedback] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      toast.error("Please sign in to provide feedback");
      return;
    }

    setIsSubmitting(true);

    // Check if updating existing vote or creating new one
    if (hasExistingVote) {
      const { error: updateError } = await supabase
        .from('help_feedback' as any)
        .update({
          was_helpful: false,
          feedback_text: feedback.trim() || null,
        })
        .eq('article_id', articleId)
        .eq('user_id', user.id);

      if (updateError) {
        toast.error("Failed to update feedback");
        setIsSubmitting(false);
        return;
      }

      toast.success("Your feedback has been updated! We'll use it to improve our help content.");
    } else {
      const { error: insertError } = await supabase
        .from('help_feedback' as any)
        .insert({
          article_id: articleId,
          user_id: user.id,
          was_helpful: false,
          feedback_text: feedback.trim() || null,
        });

      if (insertError) {
        toast.error("Failed to submit feedback");
        setIsSubmitting(false);
        return;
      }

      toast.success("Thank you for your feedback! We'll use it to improve our help content.");
    }

    setIsSubmitting(false);
    onSubmit();
    onOpenChange(false);
    setFeedback("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border">
        <DialogHeader>
          <DialogTitle>Help us improve</DialogTitle>
          <DialogDescription>
            We're sorry this article wasn't helpful. Please let us know what we can do better.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Textarea
            placeholder="What was missing or confusing? (optional)"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            className="min-h-[100px] bg-background border-border"
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit Feedback"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HelpFeedbackDialog;
